import Loader from '../../components/Loader';
import {T} from '../../utils/I18n/I18n';
import {useTranslation} from "react-i18next";
import clsx from 'clsx';
import React, {useContext, useEffect, useState} from 'react';
import BusinessUserPng from '../../assets/icons/checkout-business.png';
import PrivateUserPng from '../../assets/icons/checkout-private.png';
import {useCartContext} from '../../context/CartContext';
import {usePopup} from '../../context/PopupContext';
import {checkoutEvent} from '../../hooks/useAnalytics';
import useApplyCoupon from '../../hooks/useApplyCoupon';
import CheckoutLoyalty from './CheckoutLoyalty';
import CheckoutPaymentMethods from './CheckoutPaymentMethods';
import './Checkout.scss';
import readConfig from "../../utils/readClientConfiguration";
import productionConfiguration from '../../config/default.json';
import BillingFormFields from "./BillingFormFields";

const TYPE_PRIVATE = 1;
const TYPE_BUSINESS = 2;



const CheckoutPromoCode = () => {
  // const { t } = useTranslation();
  const { t, i18n } = useTranslation();
  const { showAddToCartPopupMessage } = usePopup();
  const [promoCode, setPromoCode] = useState('');
  const { applyCoupon, loading, removeCoupon, removeLoading, error, removeError } = useApplyCoupon();
  const { cart } = useCartContext();

  useEffect(() => {
    if (cart && cart.applied_coupons && cart.applied_coupons[0]) {
      setPromoCode(cart.applied_coupons[0].code);
    }
  }, [cart]);

  useEffect(() => {
    if (error || removeError) {
      showAddToCartPopupMessage(error.message || removeError.message, 'error', 4000);
    }
  }, [error, removeError]);

  const addCouponHandler = async () => {
    if (promoCode) {
      const { data } = await applyCoupon(promoCode, toString(cart && cart.cart_id));
      const { items } = data.applyCouponToCart.cart;

      if (
        // if there are no discounts applied to some items, show the partial coupon message
        items.some(item => !item.prices.discounts || item.prices.discounts.length === 0)
      ) {
        showAddToCartPopupMessage(t('checkout.couponAppliedPartial'), 'success', 3000);
      } else {
        showAddToCartPopupMessage(t('checkout.couponApplied'), 'success', 3000);
      }
    }
  };

  const removeCouponHandler = async () => {
    await removeCoupon(toString(cart && cart.cart_id));
    showAddToCartPopupMessage(t('checkout.couponRemoved'), 'success', 3000);
  };

  return (
    <div className="checkout-promo-code">
      <label>
        <T id="cartSummary.promoCode" />{' '}
      </label>

      <div className="checkout-promo-code__input">
        <input
          placeholder={t('cartSummary.enterPromoCode')}
          value={promoCode}
          onChange={e => setPromoCode(e.currentTarget.value)}
        />

        {(loading || removeLoading) && <Loader height="xl" />}

        {!cart.applied_coupons && !loading && (
          <button
            className="checkout__btn checkout__btn--primary "
            title={t('cartSummary.checkoutApply')}
            onClick={addCouponHandler}
          >
            {t('cartSummary.checkoutApply')}
          </button>
        )}

        {cart.applied_coupons && !removeLoading && (
          <button className="checkout__btn checkout__btn--primary " onClick={removeCouponHandler}>
            <T id="giftWrap.cancel" />
          </button>
        )}
      </div>
    </div>
  );
};

const CheckoutSpecialRemark = () => {
  const { t } = useTranslation();

  // Use the cart context to store order notes
  const { setOrderNote, orderNote } = useCartContext();

  return (
    <div className="checkout-special-remark">
      <label htmlFor="specialremark">
        <T id="cartSummary.orderCommentText" />
      </label>

      <textarea
        id="specialremark"
        placeholder={t('cartSummary.checkoutNotesOfDelivery')}
        onChange={e => {
          setOrderNote(e.target.value);
        }}
        value={orderNote}
      />
    </div>
  );
};

const CheckoutStep2Form = ({ setCheckoutStep }) => {
  const checkoutConfig = readConfig(productionConfiguration, 'checkout');
  const { showAddToCartPopupMessage } = usePopup();
  const { cart, refetchCart } = useCartContext();


  const [billingType, setBillingType] = useState(() => {
    if (cart && cart.billing_address) {
      return cart.billing_address.company ? TYPE_BUSINESS : TYPE_PRIVATE;
    }

    return TYPE_PRIVATE;
  });

  const { t } = useTranslation();

  useEffect(() => {
    const checkIsDisabled =
      !cart ||
      !cart.shipping_addresses ||
      !cart.shipping_addresses.length ||
      !cart.shipping_addresses[0].selected_shipping_method;

    if (checkIsDisabled) {
      showAddToCartPopupMessage(t('checkout.errorSelectShippingMethod'), 'error', 4000);
      setCheckoutStep(1);
    }
  }, []);

  useEffect(() => {
    if (cart && cart.items) {
      checkoutEvent(cart.items, 3);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    refetchCart();
  }, []);

  const method1ClassName = clsx('shipping-method', billingType === TYPE_PRIVATE && 'shipping-method--selected');
  const method2ClassName = clsx('shipping-method', billingType === TYPE_BUSINESS && 'shipping-method--selected');

  const isBillingSelected = cart && cart.billing_address;

  return (
    <>
      <div className="checkout__main">

        <div className="checkout__main__method-title">
          <T id="cartSummary.customerType" />
        </div>

        <div className="checkout__main__methods">
          <div onClick={() => setBillingType(TYPE_PRIVATE)} className={method1ClassName}>
            <div className="shipping-method__image-private">
              <img src={PrivateUserPng} alt="Pickup" />
            </div>

            <div className="shipping-method__text-2" title={t('cartSummary.privateUser')}>
              <T id="cartSummary.privateUser" />
            </div>
          </div>

          {checkoutConfig.enableCompany &&
              <div onClick={() => setBillingType(TYPE_BUSINESS)} className={method2ClassName}>
            <div className="shipping-method__image-business">
              <img src={BusinessUserPng} alt="Delivery" />
            </div>

            <div className="shipping-method__text-2" title={t('cartSummary.businessUser')}>
              <T id="cartSummary.businessUser" />
            </div>
          </div>
          }
        </div>

        {billingType !== 0 && (
          <div className="checkout__main__form">
            <BillingFormFields billingType={billingType} setBillingType={setBillingType} />
          </div>
        )}
      </div>

      {isBillingSelected && (
        <>
          <CheckoutLoyalty />
          <CheckoutPromoCode />
          <CheckoutSpecialRemark />
          <CheckoutPaymentMethods />
        </>
      )}
    </>
  );
};

export default CheckoutStep2Form;
